<template>
  <div class="file-uploader">
    <el-upload
        ref="uploadRef"
        :action="action"
        :headers="headers"
        :list-type="listType"
        :multiple="multiple"
        :limit="limit"
        :file-list="fileList"
        :on-success="handleUploadSuccess"
        :on-error="handleUploadError"
        :on-exceed="handleExceed"
        :before-upload="handleBeforeUpload"
        :on-remove="handleRemove"
        :on-preview="handlePreview"
        :class="{ 'hide-upload': fileList.length >= limit }"
    >
      <el-button size="small" type="primary">点击上传</el-button>
      <div slot="tip" class="el-upload__tip" v-if="showTip">
        请上传
        <template v-if="fileSize">
          大小不超过 <b style="color: #f56c6c">{{ fileSize }}MB</b>
        </template>
        <template v-if="fileType.length">
          格式为 <b style="color: #f56c6c">{{ fileType.join('/') }}</b>
        </template>
      </div>
    </el-upload>

    <!-- 预览对话框 -->
    <el-dialog
        :visible.sync="previewVisible"
        title="预览"
        :width="dialogWidth"
        append-to-body
    >
      <img
          v-if="previewType === 'image'"
          :src="previewUrl"
          style="display: block; max-width: 100%; margin: 0 auto"
      />
      <div v-else>该文件类型不支持预览</div>
    </el-dialog>
  </div>
</template>

<script>
import { getToken } from '@/utils/getToken'  // 根据实际路径引入
import { Message, Loading } from 'element-ui'

export default {
  name: 'FileUploader',
  props: {
    // 绑定值（用于 v-model）
    value: {
      type: Array,
      default: () => []
    },
    // 上传地址
    action: {
      type: String,
      required: true
    },
    // 请求头
    headers: {
      type: Object,
      default: () => ({ Authorization: 'Bearer ' + JSON.parse(getToken("token")).token })
    },
    // 文件数量限制
    limit: {
      type: Number,
      default: 5
    },
    // 单个文件大小限制（MB）
    fileSize: {
      type: Number,
      default: 5
    },
    // 允许的文件类型，例如 ['jpg', 'png', 'pdf']
    fileType: {
      type: Array,
      default: () => []
    },
    // 是否显示提示
    showTip: {
      type: Boolean,
      default: true
    },
    // 列表类型：picture-card / text / picture
    listType: {
      type: String,
      default: 'picture-card'
    },
    // 是否多选
    multiple: {
      type: Boolean,
      default: true
    },
    // 预览对话框宽度
    dialogWidth: {
      type: String,
      default: '800px'
    }
  },
  data() {
    return {
      fileList: [],            // 当前展示的文件列表
      uploadingCount: 0,       // 正在上传的数量
      previewVisible: false,
      previewUrl: '',
      previewType: '',
      loadingInstance: null
    }
  },
  watch: {
    // 监听外部 value 变化，同步到内部 fileList
    value: {
      handler(val) {
        this.fileList = val.map(item => {
          if (typeof item === 'string') {
            return { url: item, name: item.split('/').pop() }
          }
          return item
        })
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    // 上传前校验与压缩
    handleBeforeUpload(file) {
      // 类型校验
      if (this.fileType.length > 0) {
        const fileExt = file.name.split('.').pop().toLowerCase()
        const isValid = this.fileType.some(type => {
          return file.type.includes(type) || fileExt === type.toLowerCase()
        })
        if (!isValid) {
          Message.error(`文件格式不正确，请上传 ${this.fileType.join('/')} 格式的文件`)
          return false
        }
      }

      // 文件名不能包含逗号（原业务需求）
      if (file.name.includes(',')) {
        Message.error('文件名不能包含英文逗号')
        return false
      }

      // 大小限制
      if (this.fileSize) {
        const isLt = file.size / 1024 / 1024 < this.fileSize
        if (!isLt) {
          Message.error(`文件大小不能超过 ${this.fileSize}MB`)
          return false
        }
      }
      // 增加计数并显示 loading
      this.uploadingCount++
      if (!this.loadingInstance) {
        this.loadingInstance = Loading.service({
          fullscreen: true,
          text: '正在上传...',
          background: 'rgba(0,0,0,0.7)'
        })
      }
      // 图片压缩（仅对图片类型）
      if (file.type.startsWith('image/')) {
        return this.compressImage(file).then(compressedFile => {
          return compressedFile
        }).catch(() => {
          Message.error('图片压缩失败，将使用原文件')
          return file
        })
      }
      return file
    },

    // 图片压缩方法（从 Vue 3 示例移植）
    compressImage(file, quality = 0.8) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const img = new Image()
          img.onload = () => {
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')
            const scale = Math.min(1, Math.sqrt(500 * 1024 / file.size))
            canvas.width = img.width * scale
            canvas.height = img.height * scale
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
            const mimeType = file.type.startsWith('image/') ? file.type : 'image/jpeg'
            canvas.toBlob((blob) => {
              const compressedFile = new File([blob], file.name, {
                type: mimeType,
                lastModified: Date.now()
              })
              if (compressedFile.size > 500 * 1024 && quality > 0.2) {
                resolve(this.compressImage(compressedFile, quality * 0.8))
              } else {
                resolve(compressedFile)
              }
            }, 'image/jpeg', quality)
          }
          img.src = e.target.result
        }
        reader.readAsDataURL(file)
      })
    },

    // 上传成功回调
    handleUploadSuccess(res, file) {
      if (res.code === 200) {
        const newFile = {
          name: res.newFileName || file.name,
          url: res.url,
          data: res.data
        }
        // 立即更新 fileList 并通知父组件
        this.fileList.push(newFile)
        this.$emit('input', this.fileList)
      } else {
        Message.error(res.msg || '上传失败')
        this.$refs.uploadRef.handleRemove(file)
      }

      this.uploadingCount--
      this.closeLoadingIfComplete()
    },

    // 上传失败
    handleUploadError(err, file) {
      Message.error('上传失败，请重试')
      this.$refs.uploadRef.handleRemove(file)
      this.uploadingCount--
      this.closeLoadingIfComplete()
    },

    // 超出数量限制
    handleExceed() {
      Message.error(`最多上传 ${this.limit} 个文件`)
    },

    // 删除文件
    handleRemove(file, fileList) {
      this.fileList = fileList
      // 更新父组件 v-model
      this.$emit('input', fileList)
    },

    // 预览
    handlePreview(file) {
      const isImage = file.url && file.url.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i)
      if (isImage) {
        this.previewType = 'image'
        this.previewUrl = file.url.startsWith('http') ? file.url : (process.env.VUE_APP_BASE_API + file.url)
        this.previewVisible = true
      } else {
        Message.info('该文件类型不支持预览')
      }
    },
    closeLoadingIfComplete() {
      if (this.uploadingCount <= 0 && this.loadingInstance) {
        this.loadingInstance.close()
        this.loadingInstance = null
        this.uploadingCount = 0
      }
    },
    // // 上传结束处理
    // uploadedSuccessfully() {
    //   if (this.uploadingCount > 0 && this.tempUploadList.length === this.uploadingCount) {
    //     // 合并文件列表
    //     const newList = this.fileList
    //         .filter(f => f.url !== undefined)
    //         .concat(this.tempUploadList)
    //     this.fileList = newList
    //     this.$emit('input', newList)
    //
    //     this.tempUploadList = []
    //     this.uploadingCount = 0
    //     this.closeLoading()
    //   }
    // },

    // // 关闭加载
    // closeLoading() {
    //   if (this.loadingInstance) {
    //     this.loadingInstance.close()
    //     this.loadingInstance = null
    //   }
    // }
  }
}
</script>

<style scoped>
.file-uploader .hide-upload /deep/ .el-upload--picture-card {
  display: none;
}
</style>